import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Typography,
  Stack,
  CircularProgress,
  Chip,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PageHeader, SectionCard, EmptyState } from "_shared/ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { toastMessage } from "utils/toastMessage";
import { Toaster } from "react-hot-toast";
import { makeDealerOnboardStorageKey, clearPersistedDealerOnboard } from "utils/persistForm";
import AccessController from "_shared/AccessController";
import TopBar from "_shared/Top";
import { DashGridL2 } from "layouts/dash-grid";

const isHttpUrl = (value) => {
  if (!value || typeof value !== "string") return false;
  return /^https?:\/\//i.test(value);
};

const Section = ({ title, children }) => (
  <Box sx={{ mt: 4, mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
      <Box
        sx={{
          width: 4,
          height: 20,
          bgcolor: "primary.main",
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 15,
          lineHeight: 1.2,
          color: "text.primary",
          textTransform: "uppercase",
          letterSpacing: 0.6,
        }}
      >
        {title}
      </Typography>
    </Box>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Box>
);

// Prominent header separating the two onboarding phases (mirrors the form's steps).
const PhaseHeader = ({ index, title }) => (
  <Box
    sx={{
      mt: 4,
      mb: 1,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2,
      py: 1.25,
      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
      border: "1px solid",
      borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        backgroundColor: "primary.main",
        color: "primary.contrastText",
        fontWeight: 800,
        fontSize: 14,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {index}
    </Box>
    <Typography sx={{ fontWeight: 800, fontSize: 16, letterSpacing: 0.3 }}>
      {title}
    </Typography>
  </Box>
);

const FieldRow = ({ label, value }) => (
  <Box
    sx={{
      p: 1.5,
      bgcolor: "background.default",
      border: "1px solid",
      borderColor: "divider",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      transition: "transform 0.15s ease-in-out, border-color 0.15s ease-in-out",
      "&:hover": {
        borderColor: "primary.main",
        transform: "translateY(-1px)",
      },
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontWeight: 600,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: "text.primary",
        fontWeight: 700,
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const toDisplay = (field) => {
  if (!field) return "";
  const code = field?.code;
  const label = field?.label;
  if (label && code) return `${label} (${code})`;
  if (label) return label;
  if (code) return code;
  if (typeof field === "string") return field;
  return "";
};

const DocumentPreviewCard = ({ label, value, previewUrl }) => {
  const imgSrc =
    previewUrl ||
    value?.imageUrl ||
    (isHttpUrl(value?.code) ? value.code : "");

  return (
    <Box
      sx={{
        height: "100%",
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        bgcolor: "background.paper",
        transition: "transform 0.15s ease-in-out, border-color 0.15s ease-in-out",
        "&:hover": {
          borderColor: "primary.main",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <DescriptionOutlinedIcon fontSize="small" color="success" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
            {label}
          </Typography>
        </Box>
        <Chip
          size="small"
          color="success"
          variant="outlined"
          icon={<CheckCircleOutlineIcon />}
          label="Uploaded"
        />
      </Box>

      {imgSrc ? (
        <Box
          sx={{
            position: "relative",
            display: "block",
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            component="img"
            src={imgSrc}
            alt={label}
            sx={{
              width: "100%",
              height: 148,
              objectFit: "contain",
              display: "block",
              bgcolor: "action.hover",
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "action.hover",
            minHeight: 148,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            Document Number
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              wordBreak: "break-all",
            }}
          >
            {value?.code || "—"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const SUBMIT_TIMEOUT_MS = 120000;

// fetch() rejects with a bare "Failed to fetch" for any network-level failure,
// including a gateway error whose response carries no CORS header. The server
// sets Access-Control-Allow-Origin on every response it produces (preflight and
// 401s included), so a missing header means the request died upstream — surface
// that instead of the opaque browser message.
// JSON.stringify throws a TypeError on circular values (a File, a DOM node or a
// React synthetic event that leaked into state). Identify the offending field so
// the failure names something actionable instead of a generic payload error.
const findUnserializableKey = (obj) => {
  for (const key of Object.keys(obj || {})) {
    try {
      JSON.stringify(obj[key]);
    } catch (_) {
      return key;
    }
  }
  return null;
};

const describeSubmitError = (err) => {
  if (err?.name === "AbortError") {
    return "The server did not respond in time. The dealer may still have been created — check the dealer list before retrying.";
  }
  if (err instanceof TypeError) {
    return "Could not reach the onboarding service (the request failed before a response was returned). Please retry, and report this to the backend team if it persists.";
  }
  return err?.message || "Submission failed. Please try again.";
};

// Renders standalone as the /actyv-ai/dealer-form/preview page (data from
// route state) or inside a Dialog on the onboarding form (data from props,
// with onBack/onSubmitted callbacks and inDialog to skip the page Container).
const Preview = ({
  formData: formDataProp,
  prospectData: prospectDataProp,
  dealerId: dealerIdProp,
  prospectId: prospectIdProp,
  docPreviews: docPreviewsProp,
  inDialog = false,
  onBack,
  onSubmitted,
} = {}) => {
  const history = useHistory();
  const location = useLocation();
  const state = location.state || {};
  const formData = formDataProp ?? state.formData ?? {};
  const prospectData = prospectDataProp ?? state.prospectData;
  const dealerId = dealerIdProp ?? state.dealerId;
  const prospectId = prospectIdProp ?? state.prospectId;
  const docPreviews = docPreviewsProp ?? state.docPreviews ?? {};
  const goBack = onBack || (() => history.goBack());
  const [submitting, setSubmitting] = useState(false);
  const storageKey = useMemo(
    () => makeDealerOnboardStorageKey(dealerId, prospectId),
    [dealerId, prospectId]
  );

  // Grouped to mirror the two-phase onboarding form exactly:
  // Phase 1 = Basic Details, Phase 2 = Business & Commercial.
  const phases = useMemo(
    () => [
      {
        title: "Basic Details",
        sections: [
          {
            title: "Address",
            fields: [
              ["Dealership Name", "dealership_name"],
              ["Dealership Address", "dealership_add"],
              ["C/O", "care_of"],
              ["Region", "region"],
              ["District", "district"],
              ["City", "city"],
              ["Postal Code", "postal_code"],
              ["Address", "address"],
            ],
          },
          {
            title: "Contact",
            fields: [
              ["Dealer name", "contact_person"],
              ["Email", "email"],
              ["Mobile Phone", "mobile_phone"],
              ["Passport Details", "passportDetails"],
              ["Father's Name", "fatherName"],
              ["Date of Birth", "dateOfBirth"],
            ],
          },
          {
            title: "Tax & Compliance",
            fields: [
              ["GST Number", "gst_number"],
              ["PAN", "pan"],
            ],
          },
          { title: "Documents", type: "documents" },
          {
            title: "Geo & Limits",
            fields: [
              ["Latitude", "latitude"],
              ["Longitude", "longitude"],
            ],
          },
        ],
      },
      {
        title: "Business & Commercial",
        sections: [
          {
            title: "SAP & Pricing",
            fields: [
              ["Customer Group", "customer_group"],
              ["Customer Pricing Procedure", "customer_pricing_procedure"],
              ["Terms of Payment", "terms_of_payment"],
              ["Account Assignment Group", "account_assignment_group"],
            ],
          },
          {
            title: "Org Structure",
            fields: [
              ["Account Group", "account_group"],
              ["Sales Office", "sales_office"],
              ["Distribution Channel", "distribution_channel"],
              ["With SAS", "with_sas"],
            ],
          },
          {
            title: "Customer Classification",
            fields: [
              ["State", "state"],
              ["Customer Class", "customer_class"],
              ["Entity", "status"],
              ["Price Group", "price_group"],
              ["Price List", "price_list"],
            ],
          },
          {
            title: "Commercial Settings",
            fields: [
              ["Customer Statistic Group", "customer_statistic_group"],
              ["Delivering Plant", "delivering_plant"],
              ["Rebate", "rebate"],
            ],
          },
          {
            title: "Commercial Officer Details",
            fields: [
              ["Commercial Officer Name", "commercialOfficerName"],
              ["Commercial Officer Email", "commercialOfficerEmail"],
              ["Commercial Officer Phone", "commercialOfficerPhone"],
            ],
          },
          {
            title: "Extras",
            fields: [
              ["Population of the city", "populationOfCity"],
              ["Regional Office", "regionalOffice"],
              ["No. of Years in Business (Tyre only)", "noOfYearsBusiness"],
              ["Shop Status", "shopStatus"],
              ["Nearest JK Dealer (KM)", "nearestJKDealer"],
              ["Nearest Steel Wheel (KM)", "nearestSteelWheel"],
              ["Competition Brand Shop (KM)", "competitionBrandShop"],
            ],
          },
        ],
      },
    ],
    []
  );

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem("token");
      if (!prospectId) {
        toastMessage("Missing prospectId. Please reopen from the list.", "error");
        return;
      }
      if (!token) {
        toastMessage("Auth token missing. Please login again.", "error");
        return;
      }
      const endpoint = `${process.env.REACT_APP_REQUEST_ENDPOINT}/api/v1/actyv/onboarding/user`;

      // Serialize before dispatching. Inlining JSON.stringify into the fetch
      // options makes a bad payload throw a TypeError *before* the request is
      // sent, which is indistinguishable from a network failure and leaves no
      // entry in the network log. Doing it here keeps the two causes separate.
      let body;
      try {
        body = JSON.stringify({ prospectId, form: formData });
      } catch (err) {
        const badKey = findUnserializableKey(formData);
        console.error("Preview submit: payload is not serializable", { badKey, err, formData });
        toastMessage(
          badKey
            ? `The "${badKey}" field holds a value that cannot be submitted. Please re-enter or re-upload it and try again.`
            : "This form holds a value that cannot be submitted. Please re-enter the details and try again.",
          "error"
        );
        return;
      }

      // This route creates the dealer in SAP and can run long; abort rather
      // than leave the button spinning forever if the gateway stalls.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);
      let res;
      try {
        res = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
      let json = {};
      try {
        json = await res.json();
      } catch (_) {
        json = {};
      }
      const statusString = String(json?.status || "").toLowerCase();
      const appError =
        json?.success === false ||
        statusString === "error" ||
        statusString === "failed" ||
        statusString === "fail";
      if (!res.ok || appError) {
        const firstError =
          (Array.isArray(json?.errors) && json.errors[0]) ||
          (json?.data && json.data.error) ||
          json?.error;
        const msg = json?.message || firstError || `Failed (HTTP ${res.status})`;
        toastMessage(msg, "error");
        return;
      }
      toastMessage(json.message, "success");
      clearPersistedDealerOnboard(storageKey);
      if (onSubmitted) {
        onSubmitted();
      } else {
        history.push("/actyv-ai");
      }
    } catch (err) {
      console.error("Preview submit error:", err);
      toastMessage(describeSubmitError(err), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const documents = [
    ["GST Certificate", "gstCertificate"],
    ["PAN Card", "panCardPhoto"],
    ["CIBIL Score Card", "cibilScoreCard"],
    ["Aadhaar Card Front", "adhaarcardFront"],
    ["Aadhaar Card Back", "adhaarcardBack"],
    ["Cheque Scan", "chequeScan"],
    ["Residency/Address Proof", "addressProof"],
  ].filter(([_, key]) => formData?.[key]?.code || formData?.[key]?.imageUrl);

  const renderDocumentsSection = () => {
    if (documents.length === 0) return null;
    return (
      <Section key="Documents" title="Documents">
        <Grid container spacing={2}>
          {documents.map(([label, key]) => (
            <Grid item xs={12} md={6} key={key}>
              <DocumentPreviewCard
                label={label}
                value={formData?.[key]}
                previewUrl={docPreviews?.[key]}
              />
            </Grid>
          ))}
        </Grid>
      </Section>
    );
  };

  const renderSection = (section) => {
    if (section.type === "documents") return renderDocumentsSection();

    const fieldsToRender = (section.fields || [])
      .map(([label, key]) => ({
        label,
        key,
        value: toDisplay(formData?.[key]),
      }))
      .filter((f) => !!f.value);

    if (fieldsToRender.length === 0) return null;

    return (
      <Section key={section.title} title={section.title}>
        <Grid container spacing={2}>
          {fieldsToRender.map(({ label, key, value }) => (
            <Grid key={key} item xs={12} md={6}>
              <FieldRow label={label} value={value} />
            </Grid>
          ))}
        </Grid>
      </Section>
    );
  };

  const renderPhase = (phase, idx) => {
    const rendered = phase.sections.map(renderSection).filter(Boolean);
    if (rendered.length === 0) return null;
    return (
      <Box key={phase.title}>
        <PhaseHeader index={idx + 1} title={phase.title} />
        {rendered}
      </Box>
    );
  };

  if (!prospectId || Object.keys(formData).length === 0) {
    return (
      <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <EmptyState
          title="No preview data found"
          description="Please start onboarding from the dealer list."
          action={
            <Button
              variant="contained"
              color="primary"
              sx={{ fontWeight: 700 }}
              onClick={() => history.push("/actyv-ai")}
            >
              Go to Actyv AI
            </Button>
          }
        />
      </Box>
    );
  }

  const paperContent = (
      <SectionCard
        sx={{
          position: "relative",
          // Inside the Dialog the card is a flex child of the paper: it must be
          // allowed to shrink and become the scroll container itself, otherwise
          // Card's default overflow:hidden clips everything below the fold.
          ...(inDialog && {
            border: "none",
            boxShadow: "none",
            minHeight: 0,
            maxHeight: "100%",
            overflowY: "auto",
          }),
        }}
        contentSx={{ p: { xs: 2, md: 2.5 } }}
      >
          <Box sx={{ mb: 2 }}>
            <PageHeader
              title="Preview Dealer Onboarding"
              subtitle="Review the details below. Click Confirm to submit or Back to edit."
              action={
                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={goBack}
                  disabled={submitting}
                  sx={{
                    color: "text.secondary",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  Back to Edit
                </Button>
              }
            />
          </Box>

          {/* Both steps done — mirrors the form's two-phase stepper */}
          <Stepper activeStep={2} alternativeLabel sx={{ mb: 2 }}>
            {phases.map((p) => (
              <Step key={p.title} completed>
                <StepLabel>{p.title}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {phases.map(renderPhase)}

          {/* Sticky Action Bar */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              mt: 3,
              mx: { xs: -2, md: -2.5 },
              px: { xs: 2, md: 2.5 },
              pt: 2,
              pb: 2,
              backgroundColor: "background.paper",
              borderTop: "1px solid",
              borderColor: "divider",
              zIndex: 2,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <Button
                variant="outlined"
                size="large"
                startIcon={<ArrowBackIcon />}
                onClick={goBack}
                disabled={submitting}
              >
                Back to Edit
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={submitting}
                onClick={handleConfirm}
                sx={{ fontWeight: 700, px: 4 }}
              >
                {submitting ? (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={20} color="inherit" />
                    <Typography variant="button" sx={{ fontWeight: 700 }}>
                      Submitting...
                    </Typography>
                  </Stack>
                ) : (
                  "Confirm & Submit"
                )}
              </Button>
            </Stack>
          </Box>
        </SectionCard>
  );

  if (inDialog) return paperContent;

  return (
    <Container maxWidth="md" sx={{ mt: 2, mb: 2 }}>
      {paperContent}
    </Container>
  );
};

const DealerOnboardPreviewPage = () => {
  return (
    <AccessController roles={["RO", "ZO", "ADMIN", "AO"]}>
      <DashGridL2 Top={TopBar} Main={Preview} />
      <Toaster />
    </AccessController>
  );
};

export { Preview as DealerOnboardPreview };
export default DealerOnboardPreviewPage;
